import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Edit, Trash2, AlertCircle, CheckCircle, Calendar, Gauge, AlertTriangle, TrendingUp, Settings, StickyNote, FileText, ArrowUp, ArrowDown, Wrench } from 'lucide-react';
import { differenceInDays, parseISO } from 'date-fns';

const getStatusInfoByDate = (endDate) => {
  const daysUntilExpiry = differenceInDays(parseISO(endDate), new Date());
  
  if (daysUntilExpiry < 0) {
    return { status: 'danger', text: 'Просрочено', icon: AlertCircle };
  } else if (daysUntilExpiry <= 30) {
    return { status: 'warning', text: `${daysUntilExpiry} дн.`, icon: AlertCircle };
  } else {
    return { status: 'ok', text: `${daysUntilExpiry} дн.`, icon: CheckCircle };
  }
};

const getStatusInfoByMileage = (currentMileage, endMileage) => {
  // end_mileage - это абсолютный пробег когда нужно обслуживание
  // Рассчитываем оставшийся пробег
  const kmRemaining = endMileage - currentMileage;
  
  if (kmRemaining <= 0) {
    // Отрицательное значение = превышение
    return { status: 'danger', text: kmRemaining === 0 ? 'Требуется обслуживание' : `Превышено на ${Math.abs(kmRemaining).toLocaleString()} км`, icon: AlertCircle };
  } else if (kmRemaining <= 1000) {
    // Красный: 1000 км или меньше
    return { status: 'danger', text: `Осталось ${kmRemaining.toLocaleString()} км`, icon: AlertCircle };
  } else {
    // Зеленый: больше 1000 км
    return { status: 'ok', text: `Осталось ${kmRemaining.toLocaleString()} км`, icon: CheckCircle };
  }
};

export default function BusCard({ bus, parameterTypes = [], onEdit, onDelete, onMoveUp, onMoveDown, isFirst, isLast, onSendToMechanic }) {
  // Подсчитываем статистику параметров
  const paramStats = React.useMemo(() => {
    if (!bus.parameters || bus.parameters.length === 0) {
      return { total: 0, danger: 0, warning: 0, ok: 0 };
    }
    
    const stats = { total: bus.parameters.length, danger: 0, warning: 0, ok: 0 };
    
    bus.parameters.forEach(param => {
      const trackingType = param.tracking_type || 'date';
      let statusInfo;
      
      if (trackingType === 'mileage') {
        statusInfo = getStatusInfoByMileage(bus.mileage, param.end_mileage);
      } else {
        statusInfo = getStatusInfoByDate(param.end_date);
      }
      
      stats[statusInfo.status]++;
    });
    
    return stats;
  }, [bus.parameters, bus.mileage]);
  
  // Определяем общий статус автобуса на основе КРИТИЧНЫХ параметров
  const busHealthStatus = React.useMemo(() => {
    if (!bus.parameters || bus.parameters.length === 0) {
      return 'ok';
    }
    
    let hasCriticalDanger = false;
    let hasCriticalWarning = false;
    
    bus.parameters.forEach(param => {
      // Находим тип параметра
      const paramType = parameterTypes.find(pt => pt.id === param.parameter_type_id);
      
      // Если тип не найден или параметр не критичный, пропускаем
      if (!paramType || paramType.is_critical === false) {
        return;
      }
      
      // Проверяем статус критичного параметра
      const trackingType = param.tracking_type || 'date';
      let statusInfo;
      
      if (trackingType === 'mileage') {
        statusInfo = getStatusInfoByMileage(bus.mileage, param.end_mileage);
      } else {
        statusInfo = getStatusInfoByDate(param.end_date);
      }
      
      if (statusInfo.status === 'danger') {
        hasCriticalDanger = true;
      } else if (statusInfo.status === 'warning') {
        hasCriticalWarning = true;
      }
    });
    
    return hasCriticalDanger ? 'danger' : hasCriticalWarning ? 'warning' : 'ok';
  }, [bus.parameters, bus.mileage, parameterTypes]);

  // Compute mechanic icon color based on ALL expired params
  const mechanicIconStatus = React.useMemo(() => {
    if (!bus.parameters || bus.parameters.length === 0) return 'ok';
    
    let hasExpiredCritical = false;
    let hasExpiredNonCritical = false;
    
    bus.parameters.forEach(param => {
      const paramType = parameterTypes.find(pt => pt.id === param.parameter_type_id);
      const isCritical = paramType?.is_critical !== false;
      const trackingType = param.tracking_type || 'date';
      let isExpired = false;
      
      if (trackingType === 'mileage' && param.end_mileage) {
        const km = param.end_mileage - (bus.mileage || 0);
        if (km <= 1000) isExpired = true;
      } else if (trackingType === 'date' && param.end_date) {
        const days = Math.floor((new Date(param.end_date) - new Date()) / (1000 * 60 * 60 * 24));
        if (days <= 30) isExpired = true;
      }
      
      if (isExpired) {
        if (isCritical) hasExpiredCritical = true;
        else hasExpiredNonCritical = true;
      }
    });
    
    if (hasExpiredCritical) return 'critical';
    if (hasExpiredNonCritical) return 'non_critical';
    return 'ok';
  }, [bus.parameters, bus.mileage, parameterTypes]);
  
  return (
    <Card className="bus-card bg-white shadow-lg border-0 hover:shadow-2xl overflow-hidden transition-all duration-300 transform hover:scale-[1.005] ring-1 ring-slate-900/5" data-testid="bus-card">
      {/* Accent top bar based on status */}
      <div className={`h-1.5 ${
        busHealthStatus === 'danger' ? 'bg-gradient-to-r from-red-500 to-red-600' :
        busHealthStatus === 'warning' ? 'bg-gradient-to-r from-amber-400 to-orange-500' :
        'bg-gradient-to-r from-emerald-400 to-green-500'
      }`} />
      <CardContent className="p-4 sm:p-6">
        {/* Status Summary Bar */}
        {paramStats.total > 0 && (
          <div className="mb-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-3 sm:p-4 border-2 border-gray-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
                  busHealthStatus === 'danger' ? 'bg-gradient-to-br from-red-500 to-red-600' :
                  busHealthStatus === 'warning' ? 'bg-gradient-to-br from-yellow-500 to-orange-500' :
                  'bg-gradient-to-br from-green-500 to-green-600'
                }`}>
                  {busHealthStatus === 'danger' ? (
                    <AlertCircle className="w-7 h-7 text-white" strokeWidth={2.5} />
                  ) : busHealthStatus === 'warning' ? (
                    <AlertTriangle className="w-7 h-7 text-white" strokeWidth={2.5} />
                  ) : (
                    <CheckCircle className="w-7 h-7 text-white" strokeWidth={2.5} />
                  )}
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Статус обслуживания</div>
                  <div className="text-lg font-black text-gray-900">
                    {busHealthStatus === 'danger' ? 'Требуется внимание' :
                     busHealthStatus === 'warning' ? 'Скоро обслуживание' :
                     'Всё в порядке'}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {paramStats.danger > 0 && (
                  <div className="flex items-center gap-1.5 bg-red-100 text-red-700 px-3 py-1.5 rounded-lg border-2 border-red-300">
                    <AlertCircle className="w-4 h-4" strokeWidth={2.5} />
                    <span className="text-sm font-bold">{paramStats.danger}</span>
                    <span className="text-xs font-semibold hidden sm:inline">просрочено</span>
                  </div>
                )}
                {paramStats.warning > 0 && (
                  <div className="flex items-center gap-1.5 bg-yellow-100 text-yellow-700 px-3 py-1.5 rounded-lg border-2 border-yellow-300">
                    <AlertTriangle className="w-4 h-4" strokeWidth={2.5} />
                    <span className="text-sm font-bold">{paramStats.warning}</span>
                    <span className="text-xs font-semibold hidden sm:inline">скоро</span>
                  </div>
                )}
                {paramStats.ok > 0 && (
                  <div className="flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1.5 rounded-lg border-2 border-green-300">
                    <CheckCircle className="w-4 h-4" strokeWidth={2.5} />
                    <span className="text-sm font-bold">{paramStats.ok}</span>
                    <span className="text-xs font-semibold hidden sm:inline">в норме</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg border-2 border-blue-300">
                  <Settings className="w-4 h-4" strokeWidth={2.5} />
                  <span className="text-sm font-bold">{paramStats.total}</span>
                  <span className="text-xs font-semibold hidden sm:inline">всего</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Left side - Photo and Mileage */}
          <div className="flex-shrink-0 w-full lg:w-72 space-y-4">
            <div className="w-full rounded-xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 shadow-xl ring-4 ring-blue-100 hover:ring-blue-300 transition-all" style={{aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              {bus.photo ? (
                <img
                  src={bus.photo}
                  alt={`${bus.brand} ${bus.model}`}
                  className="w-full h-full object-contain"
                  data-testid="bus-photo"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                  <svg className="w-16 h-16 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-medium">Нет фото</span>
                </div>
              )}
            </div>
            <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 rounded-xl p-4 shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-blue-100 font-bold tracking-wide uppercase">Текущий пробег</div>
                <TrendingUp className="w-4 h-4 text-blue-200" strokeWidth={2} />
              </div>
              <div className="text-2xl font-black text-white drop-shadow-lg mb-1" data-testid="bus-mileage">
                {bus.mileage.toLocaleString()} км
              </div>
              {paramStats.total > 0 && (
                <div className="text-xs text-blue-200 font-medium mt-2 pt-2 border-t border-blue-400/30">
                  {paramStats.danger > 0 ? (
                    <span className="flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Требуется обслуживание
                    </span>
                  ) : paramStats.warning > 0 ? (
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Скоро ТО
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Обслуживание в норме
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Notes/Additional Info Block */}
            {bus.mileage_note && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 shadow-lg border-2 border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-amber-600" strokeWidth={2} />
                  <div className="text-xs text-amber-700 font-bold tracking-wide uppercase">Заметки</div>
                </div>
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {bus.mileage_note}
                </div>
              </div>
            )}
          </div>

          {/* Right side - Details Table */}
          <div className="flex-1 min-w-0">
            <div className="mb-4 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                    <span className="text-white text-xl font-black">🚌</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-gray-700 font-bold bg-gradient-to-r from-blue-100 to-blue-200 px-3 py-2 rounded-lg border border-blue-300 inline-flex items-center gap-2" data-testid="bus-plate">
                        <span className="text-lg">🚌</span>
                        <span className="font-mono tracking-wide">{bus.license_plate}</span>
                      </span>
                      <h3 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight" data-testid="bus-title">
                        {bus.brand} {bus.model}
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 text-sm">
                  {paramStats.total > 0 && (
                    <span className="text-gray-700 font-semibold bg-gradient-to-r from-purple-100 to-purple-200 px-3 py-2 rounded-lg border border-purple-300 inline-flex items-center gap-2">
                      <Settings className="w-4 h-4 text-purple-600" strokeWidth={2} />
                      <span className="text-xs font-bold text-purple-800">{paramStats.total} параметров</span>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0 self-start">
                {/* Move Up/Down buttons */}
                <div className="flex flex-col gap-1">
                  <Button
                    onClick={onMoveUp}
                    disabled={isFirst}
                    variant="outline"
                    size="sm"
                    className="h-7 w-8 p-0 border-gray-300 hover:bg-gray-100 disabled:opacity-30"
                    title="Переместить вверх"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={onMoveDown}
                    disabled={isLast}
                    variant="outline"
                    size="sm"
                    className="h-7 w-8 p-0 border-gray-300 hover:bg-gray-100 disabled:opacity-30"
                    title="Переместить вниз"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Mechanic wrench icon */}
                {onSendToMechanic && (
                  <Button
                    data-testid="send-to-mechanic-button"
                    onClick={() => onSendToMechanic(bus)}
                    variant="outline"
                    size="sm"
                    className={`shadow-md hover:shadow-lg transform hover:scale-110 transition-all font-bold border-2 ${
                      mechanicIconStatus === 'critical'
                        ? 'border-red-400 text-red-600 bg-red-50 hover:bg-red-100'
                        : mechanicIconStatus === 'non_critical'
                        ? 'border-gray-400 text-gray-500 bg-gray-50 hover:bg-gray-100'
                        : 'border-green-400 text-green-600 bg-green-50 hover:bg-green-100'
                    }`}
                    title={
                      mechanicIconStatus === 'critical' ? 'Есть критические просрочки' :
                      mechanicIconStatus === 'non_critical' ? 'Есть просроченные параметры' :
                      'Все параметры в норме'
                    }
                  >
                    <Wrench className="w-4 h-4" />
                  </Button>
                )}

                {onEdit && (
                  <Button
                    data-testid="edit-bus-button"
                    onClick={() => onEdit(bus)}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-110 transition-all font-bold"
                    size="sm"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Изменить
                  </Button>
                )}
                {onDelete && (
                  <Button
                    data-testid="delete-bus-button"
                    onClick={() => onDelete(bus.id)}
                    variant="outline"
                    size="sm"
                    className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-500 shadow-md hover:shadow-lg transform hover:scale-110 transition-all font-bold"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Excel-style table */}
            {bus.parameters && bus.parameters.length > 0 ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-gray-700 bg-gray-50 min-w-[140px] sm:min-w-[180px]">Параметр</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-gray-700 bg-gray-50 min-w-[100px] sm:min-w-[120px]">Начало</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-gray-700 bg-gray-50 min-w-[100px] sm:min-w-[120px]">Окончание</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-gray-700 bg-gray-50 min-w-[100px] sm:min-w-[120px]">Статус</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-gray-700 bg-gray-50 min-w-[120px] sm:min-w-[150px]">Описание</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bus.parameters.map((param, index) => {
                        const trackingType = param.tracking_type || 'date';
                        let statusInfo;
                        let startValue, endValue;
                        
                        if (trackingType === 'mileage') {
                          statusInfo = getStatusInfoByMileage(bus.mileage, param.end_mileage);
                          startValue = param.start_mileage !== null && param.start_mileage !== undefined ? `${param.start_mileage.toLocaleString()} км` : '—';
                          endValue = param.end_mileage !== null && param.end_mileage !== undefined ? `${param.end_mileage.toLocaleString()} км` : '—';
                        } else {
                          statusInfo = getStatusInfoByDate(param.end_date);
                          // Формат даты yyyy/mm/dd
                          startValue = param.start_date ? param.start_date.replace(/-/g, '/') : '—';
                          endValue = param.end_date ? param.end_date.replace(/-/g, '/') : '—';
                        }
                        
                        return (
                          <tr key={param.id || index} className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0">
                            <td className="px-3 py-2 sm:px-4 sm:py-3 font-semibold text-gray-900">
                              <div className="flex items-center gap-2">
                                {trackingType === 'mileage' ? (
                                  <Gauge className="w-4 h-4 text-blue-500" />
                                ) : (
                                  <Calendar className="w-4 h-4 text-green-500" />
                                )}
                                {param.parameter_name}
                              </div>
                            </td>
                            <td className="px-3 py-2 sm:px-4 sm:py-3 text-gray-700 text-xs sm:text-sm">{startValue}</td>
                            <td className="px-3 py-2 sm:px-4 sm:py-3 text-gray-700 text-xs sm:text-sm">{endValue}</td>
                            <td className="px-3 py-2 sm:px-4 sm:py-3">
                              <span className={`status-badge status-${statusInfo.status} text-xs`}>
                                <statusInfo.icon className="w-3 h-3 mr-1" />
                                {statusInfo.text}
                              </span>
                            </td>
                            <td className="px-3 py-2 sm:px-4 sm:py-3 text-gray-600 text-xs">{param.description || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg bg-gray-50 p-8 text-center">
                <p className="text-gray-500 text-sm">Параметры не добавлены</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}